import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { Stack, Typography } from "@mui/material";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { CSVLink } from "react-csv";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { APP_ICONS } from "../../../assets";
import AppLoader from "../../../components/AppLoader";
import PaperContainer from "../../../components/Containers/PaperContainer";
import GlobalButton from "../../../components/CustomButton";
import StatusBlockDialog from "../../../components/Dialogs/StatusBlockDialog";
import DynamicPaginatedTable from "../../../components/Tables/DynamicPaginatedTable";
import TextInput from "../../../components/TextInputs";
import useDebounce from "../../../hooks/useDebounce";
import { setSearchKeys } from "../../../lib/slices/searchKeysSlice";
import { updateAllAcountStatus } from "../../../services/commonApiFn";
import { getAllUsers } from "../../../services/usersApiFn";
import { handleOpenSnackBar } from "../../../utils";

const headerData = ["createdAt", "clientDetails", "signupAs", "status"];

const tableRows = [
  "created_date",
  "client_details",
  "client_sign_up_as",
  "status",
];

function ClientLists() {
  const { t } = useTranslation(["client", "common"]);
  const dispatch = useDispatch();
  const openBlockStatusDialog = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [allUsers, setAllUsers] = useState();
  const searchKeys = useSelector((state) => state.searchKeysReducer.searchKeys);
  const debouncedSearchTerm = useDebounce(searchKeys.userByName, 1000);

  const fetchAllUsers = async (name) => {

    const params = {
      search: name,
      limit: 500
    }

    try {
      setIsLoading(true);
      const response = await getAllUsers(params);
      if (response.data.status === "success") {
        const data = response.data.data.allUsers;
        setAllUsers(data);
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAllUsers(debouncedSearchTerm);
  }, [debouncedSearchTerm]);

  const handleOpenConfirmDialog = (e, row) => {
    openBlockStatusDialog.current.openDialog({
      type: "Client",
      status: e ? "Unblock" : "Block",
      data: row,
      event: e,
    });
  };

  const handleUpdateUserStatus = async (params) => {
    try {
      let payload = {
        status: params?.event,
        reason: params.reason,
      };
      setIsLoading(true);
      const response = await updateAllAcountStatus(
        params?.data?._id,
        payload,
        "Client"
      );
      if (response.data.status === "success") {
        fetchAllUsers(searchKeys.userByName);
        handleOpenSnackBar("success", response.data.message);
        openBlockStatusDialog.current.closeDialog({});
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const prepareExportData = () => {
    return allUsers?.map((item) => ({
      "Created Date": moment(item.createdAt).format("MM-DD-YYYY"),
      "Client Name": `${item.firstname} ${item.lastname}`,
      Email: item.email,
      Contact: item.contact,
    }));
  };

  const renderHeaderAction = () => {
    return (
      <Stack flexGrow={1} direction={{ md: "row" }} justifyContent={"flex-end"}>
        <CSVLink
          data={prepareExportData()}
          filename={"client_list.csv"}
          className="csv-link"
          target="_blank"
        >
          <GlobalButton
            variant={"export_button"}
            btnText={"export"}
            startIcon={<img src={APP_ICONS.exportBlockIcon} width={20} />}
          />
        </CSVLink>
      </Stack>
    );
  };

  const renderSearchAction = () => {
    return (
      <Stack p={2} direction={{ md: "row" }} bgcolor={"primary.light"}>
        <TextInput
          styleProps={{ width: { xs: "100%", md: "35%", xl: "30%" } }}
          InputStartIcon={<img src={APP_ICONS.searchGreyIcon} width={25} />}
          InputEndIcon={
            searchKeys.userByName && (
              <CancelOutlinedIcon
                fontSize="small"
                sx={{ cursor: "pointer" }}
                onClick={() => {
                  dispatch(
                    setSearchKeys({
                      ...searchKeys,
                      userByName: "",
                    })
                  );
                }}
              />
            )
          }
          placeholder={t("searchByName", { ns: "common" })}
          value={searchKeys.userByName}
          onChange={(e) => {
            dispatch(
              setSearchKeys({
                ...searchKeys,
                userByName: e.target.value,
              })
            );
          }}
        />
      </Stack>
    );
  };

  return (
    <>
      <AppLoader isVisible={isLoading} />
      <PaperContainer
        title={t("clientLists")}
        subtitle={t("manageClients")}
        // headerActions={allUsers?.length > 0 ? renderHeaderAction() : null}
        searchActions={renderSearchAction()}
      >
        <DynamicPaginatedTable
          showPagination={true}
          tableHeader={headerData}
          displayRows={tableRows}
          tableData={allUsers}
          handleChangeStatus={handleOpenConfirmDialog}
        />
        {allUsers?.length === 0 && isLoading === false && (
          <Typography variant="h5" fontWeight={600} textAlign={"center"}>
            {t("noDataFound", { ns: "common" })}
          </Typography>
        )}
      </PaperContainer>
      <StatusBlockDialog
        ref={openBlockStatusDialog}
        onConfirm={handleUpdateUserStatus}
      />
    </>
  );
}

export default ClientLists;
